import type { BlockId, BlockType } from '@unifyapps/defs/types/block';
import { noop } from 'lodash';
import getBlockPipeline from './blockPipeline';

/**
 * Collects every blockId a parent references through `component.slots` — i.e.
 * blocks that are a fixed *slot* of their parent (Card body, Button body, and
 * any other slot-bearing component) rather than a freely-added child. One pass
 * over all blocks; the pipeline's other handlers are intentionally no-ops
 * because tabs / stack children / repeater children are dynamic content, not
 * slots.
 */
export function getSlotBlockIds(blocks: Record<string, BlockType | undefined>): Set<BlockId> {
  const slotBlockIds = new Set<BlockId>();
  const { blocksPipeline } = getBlockPipeline();

  for (const block of Object.values(blocks)) {
    if (!block) continue;
    blocksPipeline.execute({
      block,
      handleSlotsBlock: (slotBlock) => {
        Object.values(slotBlock.component.slots ?? {}).forEach((slot) => {
          if (slot.blockId) slotBlockIds.add(slot.blockId);
        });
      },
      handleContentBlock: noop,
      handleBlockIdsBlock: noop,
      handleBlockIdBlock: noop,
    });
  }

  return slotBlockIds;
}

/**
 * A block is a "slot" when it's a fixed structural region of its parent
 * (Card body, Button body, …) referenced through the parent's
 * `component.slots`. Page root regions (body / header / footer) are NOT slots.
 * `slotBlockIds` is the precomputed set from getSlotBlockIds — pass it in so a
 * caller tagging many crumbs builds the set once.
 */
export function getIsSlotBlock(blockId: BlockId | undefined, slotBlockIds: Set<BlockId>): boolean {
  return blockId != null && slotBlockIds.has(blockId);
}
